// FILE: serverOrigin.ts
// Purpose: Resolve the HTTP origin of the Taskin server for URLs the browser fetches directly (images, favicons).
// Exports: `resolveServerHttpOrigin`.

/**
 * Where the server lives, for subresources the browser loads by URL.
 *
 * `window.location.origin` is the right answer whenever the page was served by
 * the server itself, which is production and the desktop client. It is the
 * wrong answer in the two cases this function exists for:
 *
 *   - Vite dev, where the page comes from :5733 and the server is on :3773.
 *   - Any client configured against a remote server via `VITE_WS_URL`.
 *
 * The WebSocket URL is used as the source of truth because it is the one
 * address every client already has to know to work at all; deriving the HTTP
 * origin from it keeps a second setting from existing and drifting.
 *
 * `fetch` calls do not need this — they go through the dev proxy — but an
 * `<img src>` is resolved by the browser against the page, not the proxy.
 */
export function resolveServerHttpOrigin(): string {
  if (typeof window === "undefined") return "";

  const bridgeWsUrl = window.desktopBridge?.getWsUrl?.();
  const envWsUrl = import.meta.env.VITE_WS_URL as string | undefined;
  const wsCandidate =
    typeof bridgeWsUrl === "string" && bridgeWsUrl.length > 0
      ? bridgeWsUrl
      : typeof envWsUrl === "string" && envWsUrl.length > 0
        ? envWsUrl
        : null;

  if (!wsCandidate) return window.location.origin;

  try {
    const wsUrl = new URL(wsCandidate);
    const protocol =
      wsUrl.protocol === "wss:" ? "https:" : wsUrl.protocol === "ws:" ? "http:" : wsUrl.protocol;
    return `${protocol}//${wsUrl.host}`;
  } catch {
    return window.location.origin;
  }
}
